/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Select, TextArea } from '@telus-uds/components-web';
import { Box } from '../design/css';
import request from '../utils/request';
import { orderStatus as statusConstants } from '../utils/constant';
import ValidateLoader from '../UI/validateLoader';
import MessageNotification from '../messageNotification';
import TypeAhead from './typeAhead';
import { useLocalizedStringsContext } from '../context/LocalizedStringsContext';

const UpdateOrder = ({
  orderStatus,
  sovStatus,
  setOpen,
  open,
  order,
  onSave,
  users,
  assignedUserId,
  userRights,
  labels,
  children
}) => {
  const { incomplete, validated, waitingOnCredit } = statusConstants;
  const getOrderStatus = () => {
    if (sovStatus) return sovStatus;
    if (orderStatus === incomplete || orderStatus === validated) {
      return orderStatus;
    }
    return '';
  };

  const [status, setStatus] = useState(orderStatus || '');
  const [comment, setComment] = useState();
  const [loading, setloading] = useState();
  const [error, setError] = useState();
  const [defaultAssignee, setDefaultAssignee] = useState([]);
  const [assignee, setAssignee] = useState(defaultAssignee);

  const isDisabled = orderStatus === 'ORDER_VALIDATED';
  const isStatusChanged = status !== orderStatus;
  const isAssigneeUpdated = defaultAssignee !== assignee;
  const isOrderUpdated = isStatusChanged || comment || isAssigneeUpdated;

  const localizedString = useLocalizedStringsContext();
  const {
    ORDERS: { statusOptions }
  } = localizedString;

  const {
    pendingValidation,
    assign,
    unassigned,
    update,
    noChange,
    add,
    save,
    cancel,
    loadingContentMessage
  } = labels;
  useEffect(() => {
    if (users && assignedUserId && !assignee.length) {
      const currentAssignee = users.filter(user => user.id === assignedUserId);
      setDefaultAssignee(currentAssignee);
      setAssignee(currentAssignee);
    }
  }, [assignedUserId, users]);

  const getAssigneeId = () => {
    if (!isAssigneeUpdated) return undefined;
    return assignee.length ? assignee[0].id : -1;
  };

  const addComment = () => {
    const commentsBody = {
      text: comment
    };
    return request.post(`/orders/${order.id}/comments`, commentsBody);
  };

  const updateOrder = () => {
    setloading(true);
    const updateBody = {
      status: status === waitingOnCredit ? undefined : status,
      sovStatus: status === waitingOnCredit ? status : '',
      assignedUserId: getAssigneeId(),
      step: status === statusConstants.incomplete ? 1 : undefined
    };

    return request.put(`/orders/${order.id}`, updateBody);
  };

  const onStatusChange = (value) => {
    setStatus(value);
  };

  const closeModal = () => {
    if (isOrderUpdated && !error) {
      onSave({ isUpdated: true, orderId: order.orderId });
      setOpen(!open);
    }
  };

  const onClickSave = async () => {
    if (!isOrderUpdated) return;
    try {
      setloading(true);
      if (comment) {
        await addComment();
      }
      if (isStatusChanged || isAssigneeUpdated) {
        await updateOrder();
      }
      closeModal();
    } catch (err) {
      setError(err.message);
    }
    setloading(false);
  };

  const onClickCancel = () => {
    setOpen(!open);
  };

  const buttons = [
    {
      label: save,
      onClick: onClickSave,
      variant: 'secondary'
    },
    {
      label: cancel,
      onClick: onClickCancel,
      variant: 'primary'
    }
  ];
  return (
    <>
      {loading && <ValidateLoader message={loadingContentMessage} />}
      {userRights && userRights.canAssignOrder && (
        <Box display="flex" width={30} top={4} bottom={4}>
          <TypeAhead
            label={assign}
            onChange={value => setAssignee(value)}
            options={users}
            selected={assignee}
            disabled={isDisabled}
            placeholder={unassigned}
          />
        </Box>
      )}
      <Box>{children}</Box>
      {error && <MessageNotification variant="error" message={error} />}
      <Box display="flex" width={100} mdWidth={100} top={4} bottom={4}>
        {userRights && userRights.isStatusEditable && (
          <Box width={100}>
            <Select
              label={update}
              placeholder={pendingValidation}
              onChange={onStatusChange}
              value={status || getOrderStatus()}
              inactive={isDisabled}
              hint={isDisabled && noChange}
            >
              {statusOptions.map(option => (
                <Select.Item key={option.value} value={option.value}>
                  {option.text || option.label}
                </Select.Item>
              ))}
            </Select>
          </Box>
        )}
        <Box width={100}>
          <TextArea
            label={add}
            id="order-validation"
            inactive={isDisabled}
            onChange={(value) => setComment(value)}
          />
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent="space-evenly"
        flexDirection="row"
        width={100}
        mdWidth={100}
        right={3}
        left={3}
        top={5}
        bottom={4}
      >
        {buttons.map(({ label, onClick, variant }) => {
          return (
            <Button variant={variant} onClick={onClick}>
              {label}
            </Button>
          );
        })}
      </Box>
    </>
  );
};

UpdateOrder.propTypes = {
  orderStatus: PropTypes.string.isRequired,
  sovStatus: PropTypes.string.isRequired,
  setOpen: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  order: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  users: PropTypes.array.isRequired,
  assignedUserId: PropTypes.number.isRequired,
  userRights: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired
};
export default UpdateOrder;
